import { createHTTPClient } from "@airhelp/http-client";
import { caller, valid, queryStringAddon, snakeCaseBody } from "@airhelp/http-client/addons";
import { F as FeatureFlagState, T as TripTier, A as ActivationStatus, a as AirPayoutStatus, b as AirPayoutDisruption, c as AirPayoutCoverage, d as AirPayoutEligibleReason, e as AirLuggageStatus, f as AirBundleRejectionReason, C as CheckDeletableReason, I as InsuranceType, g as AirBundleDocumentParam, P as ProductName, M as MembershipTier, S as StripePaymentStatues } from "./enums-C8iMtmI9.js";
import { l, k, B, h, j, i, R } from "./enums-C8iMtmI9.js";
import { b, a, f, m, o } from "./fetch-offers-Dxzm5j4P.js";
import { z } from "zod";
const featureFlagSchema = z.object({
  key: z.string(),
  state: z.nativeEnum(FeatureFlagState)
});
const loungeSchema = z.object({
  additional: z.string(),
  airportCode: z.string(),
  airportName: z.string(),
  city: z.string(),
  conditions: z.string(),
  country: z.string(),
  countryCode: z.string(),
  images: z.array(
    z.object({
      url: z.string().url(),
      width: z.number(),
      height: z.number(),
      altText: z.string(),
      contentType: z.string(),
      lastModified: z.string()
    })
  ),
  location: z.string(),
  loungeCode: z.string(),
  loungeName: z.string(),
  openingHours: z.string(),
  terminal: z.string(),
  terminalCode: z.string()
});
const tripSchema$1 = z.object({
  id: z.number(),
  uuid: z.string(),
  tier: z.nativeEnum(TripTier).nullable(),
  startOn: z.string(),
  endOn: z.string(),
  brandName: z.string().nullable(),
  coveredPassengersQuantity: z.number(),
  upgradeable: z.boolean()
});
const tripResponseSchema = z.object({
  trip: tripSchema$1
});
const fetchTripRequestSchema = z.object({
  uuid: z.string()
});
function fetchTrip({
  client,
  params
}) {
  const { uuid } = fetchTripRequestSchema.parse(params);
  return client.get(`/api/v2/trips/${uuid}`).valid(tripResponseSchema);
}
const validateActivationTokenResponseSchema = z.object({
  activationStatus: z.nativeEnum(ActivationStatus),
  email: z.string(),
  resetPasswordToken: z.string()
});
const validateActivationTokenRequestSchema = z.object({
  tripUuid: z.string(),
  token: z.string()
});
function validateActivationToken({
  client,
  params
}) {
  const { tripUuid, token } = validateActivationTokenRequestSchema.parse(params);
  return client.url(`/api/v2/trips/${tripUuid}/validate_activation_token`).post({ token }).valid(validateActivationTokenResponseSchema);
}
const activateTripRequestSchema = z.object({
  tripUuid: z.string(),
  token: z.string()
});
function activateTrip({
  client,
  params
}) {
  const { tripUuid, token } = activateTripRequestSchema.parse(params);
  return client.url(`/api/v2/trips/${tripUuid}/activate`).post({ token }).res();
}
const selectInput = z.object({ label: z.string(), value: z.string() });
const bankFieldsSchema = z.object({
  aba: z.string().nullable(),
  accountNumber: z.string().nullable(),
  clabe: z.string().nullable(),
  bankName: z.string().nullable(),
  bankAddress: z.string().nullable(),
  branchName: z.string().nullable(),
  branchCode: z.string().nullable(),
  bankCode: z.string().nullable(),
  branchAgencyCode: z.string().nullable(),
  iban: z.string().nullable(),
  routingNumber: z.string().nullable(),
  swift: z.string().nullable(),
  taxId: z.string().nullable(),
  bankCity: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  chineseId: z.string().nullable(),
  province: z.string().nullable(),
  ifscCode: z.string().nullable(),
  nationality: z.string().nullable(),
  bicCode: z.string().nullable(),
  sortCode: z.string().nullable(),
  paymentCode: z.string().nullable(),
  nuban: z.string().nullable(),
  bankMfo: z.string().nullable(),
  bsbCode: z.string().nullable(),
  beneficiaryAddress: z.string().nullable(),
  beneficiaryName: z.string().nullable(),
  countryOfResidence: z.string().nullable(),
  state: z.string().nullable(),
  dateOfBirth: z.coerce.date().nullable().or(z.string().nullable()),
  streetAddress1: z.string().nullable(),
  streetAddress2: z.string().nullable(),
  city: z.string().nullable(),
  zipCode: z.string().nullable(),
  boardingPassOrBookingReference: z.string().nullable(),
  pirReference: z.string().nullable(),
  passportOrId: z.string().nullable(),
  country: selectInput.nullable(),
  residence: selectInput.nullable(),
  binOrIin: z.string().nullable(),
  knpOrUpdc: z.string().nullable(),
  cancelation: z.string().nullable(),
  boardingPass: z.string().nullable(),
  bookingReference: z.string().nullable(),
  originalInvoice: z.string().nullable()
}).partial();
const airPayoutJourneysSchema = z.object({
  id: z.number(),
  status: z.nativeEnum(AirPayoutStatus),
  journeyId: z.number(),
  payoutAllowed: z.boolean().optional(),
  payoutUpdateAllowed: z.boolean().nullable().optional(),
  name: z.string().optional(),
  payoutRequested: z.boolean(),
  payoutAmount: z.number(),
  disruptionType: z.nativeEnum(AirPayoutDisruption),
  coverageType: z.nativeEnum(AirPayoutCoverage),
  ipidVersion: z.string().optional(),
  payoutEligibleReason: z.nativeEnum(AirPayoutEligibleReason).nullable()
});
const airPayoutPaymentPaxSchema = z.object({
  id: z.number(),
  boardingPassOrBookingReference: z.string().optional(),
  boardingPassOrBookingReferenceFile: z.instanceof(File).optional(),
  passportOrId: z.string().optional(),
  passportOrIdFile: z.instanceof(File).optional(),
  cancelation: z.string().optional(),
  cancelationFile: z.instanceof(File).optional(),
  boardingPass: z.string().optional(),
  boardingPassFile: z.instanceof(File).optional(),
  bookingReference: z.string().optional(),
  bookingReferenceFile: z.instanceof(File).optional(),
  originalInvoice: z.string().optional(),
  originalInvoiceFile: z.instanceof(File).optional()
});
const airPayoutRequestSchema = z.object({
  countryId: z.string(),
  bankFields: bankFieldsSchema,
  airPayoutJourneys: z.array(airPayoutPaymentPaxSchema)
});
const airPayoutsUpdateRequestSchema = z.object({
  countryId: z.string(),
  token: z.string(),
  journeyId: z.number(),
  bankFields: bankFieldsSchema
});
const airPayoutsTriggerRequestSchema = z.object({
  countryId: z.string(),
  token: z.string(),
  journeyId: z.number(),
  bankFields: bankFieldsSchema,
  airPayoutJourneys: z.array(airPayoutPaymentPaxSchema)
});
const fellowPassengerSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string()
});
const airportSchema = z.object({
  iata: z.string(),
  city: z.string(),
  cityTranslations: z.record(z.string(), z.string()),
  timeZone: z.string(),
  name: z.string()
});
const airlineSchema = z.object({
  uniqueIdentifier: z.string().nullable(),
  code: z.string().nullable(),
  iata: z.string().nullable(),
  name: z.string(),
  displayName: z.string().nullable(),
  alternativeName: z.string().nullable()
});
const flightStatusSchema = z.object({
  departureStatus: z.string().nullable(),
  arrivalStatus: z.string().nullable(),
  gmtDepartureDateTime: z.coerce.date().nullable(),
  gmtArrivalDateTime: z.coerce.date().nullable(),
  gmtEstimatedDepartureAt: z.coerce.date().nullable(),
  gmtEstimatedArrivalAt: z.coerce.date().nullable(),
  gmtActualDepartureAt: z.coerce.date().nullable(),
  gmtActualArrivalAt: z.coerce.date().nullable()
});
const flightSchema = z.object({
  id: z.number(),
  localDepartureDate: z.coerce.date(),
  flightIdentifier: z.string(),
  flightNumber: z.string(),
  gmtDepartureDateTime: z.coerce.date().nullable(),
  gmtArrivalDateTime: z.coerce.date().nullable(),
  active: z.boolean(),
  arrivalAirport: airportSchema.nullable(),
  departureAirport: airportSchema.nullable(),
  airline: airlineSchema,
  flightStatus: flightStatusSchema.nullable()
});
const itinerarySchema = z.object({
  id: z.number(),
  remindToClaim: z.boolean(),
  claimAllowed: z.boolean(),
  active: z.boolean(),
  fellowPassengers: z.array(fellowPassengerSchema),
  flights: z.array(flightSchema)
});
const airLuggageJourneysSchema = z.object({
  id: z.number(),
  status: z.nativeEnum(AirLuggageStatus),
  journeyId: z.number(),
  availableItineraries: z.array(itinerarySchema).optional(),
  payoutAllowed: z.boolean().optional(),
  payoutUpdateAllowed: z.boolean().optional(),
  name: z.string().optional(),
  payoutRequested: z.boolean(),
  payoutAmount: z.number(),
  maxBags: z.number(),
  ipidVersion: z.string().optional(),
  transactionReference: z.string().nullable().optional()
});
const airLuggagesUpdateRequestSchema = z.object({
  countryId: z.string(),
  token: z.string(),
  transactionReference: z.string().nullable().optional(),
  journeyId: z.number(),
  bankFields: bankFieldsSchema
});
const airLuggagePaymentPaxSchema = z.object({
  id: z.number(),
  numberOfLuggages: z.number(),
  payoutEligibleItineraryId: z.number(),
  pirReference: z.string().optional(),
  pirReferenceFile: z.instanceof(File).optional(),
  boardingPassOrBookingReference: z.string().optional(),
  boardingPassOrBookingReferenceFile: z.instanceof(File).optional(),
  passportOrId: z.string().optional(),
  passportOrIdFile: z.instanceof(File).optional(),
  baggageTag: z.string().optional(),
  baggageTagFile: z.instanceof(File).optional()
});
const airLuggagesTriggerRequestSchema = z.object({
  countryId: z.string(),
  token: z.string(),
  journeyId: z.number(),
  bankFields: bankFieldsSchema,
  airLuggageJourneys: z.array(airLuggagePaymentPaxSchema)
});
const airRejectionsSchema = z.object({
  airPayout: z.nativeEnum(AirBundleRejectionReason).optional(),
  airLuggage: z.nativeEnum(AirBundleRejectionReason).optional()
});
const journeySchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  active: z.boolean(),
  loungeActive: z.boolean(),
  itineraries: z.array(itinerarySchema).nonempty(),
  trip: tripSchema$1.nullable(),
  airPayoutJourneys: z.array(airPayoutJourneysSchema),
  airLuggageJourneys: z.array(airLuggageJourneysSchema),
  airRejections: airRejectionsSchema.optional(),
  displayAirBundleActionNotification: z.boolean()
});
const journeyCheckDeletableSchema = z.object({
  deletable: z.boolean(),
  loungesIncluded: z.boolean(),
  blockedBy: z.array(z.nativeEnum(CheckDeletableReason))
});
const journeyWithMetaSchema = z.object({
  journey: journeySchema,
  meta: z.object({
    sessionUrl: z.string(),
    airRequestId: z.number().optional()
  }).optional()
});
const airBundleDocumentUploadSchema = z.object({
  file: z.instanceof(File),
  insuranceType: z.nativeEnum(InsuranceType),
  documentType: z.nativeEnum(AirBundleDocumentParam),
  resourceId: z.number(),
  insuranceId: z.number()
});
const airBundlePresignedUrlSchema = z.object({
  fileType: z.string(),
  insuranceType: z.nativeEnum(InsuranceType),
  documentType: z.nativeEnum(AirBundleDocumentParam),
  resourceId: z.number(),
  insuranceId: z.number()
});
const airBundlePresignedUrlResponseSchema = z.object({
  fileName: z.string(),
  presignedUrl: z.string().url()
});
const verificationSchema = z.object({
  journeyId: z.number(),
  airRequestId: z.number().optional(),
  productType: z.nativeEnum(InsuranceType)
});
const verificationResponseSchema = z.object({
  airRequestId: z.number().optional(),
  sessionUrl: z.string()
});
const payment = z.object({
  type: z.string(),
  cardData: z.object({
    brand: z.string(),
    last4Digits: z.string(),
    expirationMonth: z.number(),
    expirationYear: z.number(),
    cardholderName: z.string()
  }).optional()
});
const subscription = z.object({
  nextChargeDate: z.string(),
  nextChargeAmount: z.string(),
  nextChargeCurrency: z.string(),
  nextChargeCurrencySymbol: z.string(),
  chargeFrequency: z.string()
});
const billingAddress = z.object({
  name: z.string(),
  city: z.string(),
  addressLine1: z.string(),
  addressLine2: z.string().nullable(),
  postalCode: z.string().nullable(),
  state: z.string().nullable(),
  countryCode: z.string()
});
const fetchBillingManagementSchema = z.object({
  payment: payment.nullable(),
  subscription,
  billingAddress: billingAddress.nullable(),
  legacy: z.boolean()
});
const dashboardSchema = z.object({
  canAddFellowPassengersToFlight: z.boolean(),
  canAddTrip: z.boolean(),
  loungesPerkEnabled: z.boolean(),
  dashboardVisited: z.boolean(),
  personalDetailsProvided: z.boolean(),
  upsellEnabled: z.boolean()
});
const benefitsSchema = z.object({
  airLuggagePayout: z.number().nullable(),
  airLuggageMaxBags: z.number().nullable(),
  airLuggageTokens: z.number().nullable(),
  airLuggageUnlimited: z.boolean().nullable(),
  airLuggageIncluded: z.boolean().nullable(),
  airPayoutPayout: z.number(),
  airPayoutTokens: z.number(),
  airPayoutUnlimited: z.boolean(),
  airPayoutIncluded: z.boolean(),
  loungeAccessEnabled: z.boolean(),
  loungeAccessUnlimited: z.boolean(),
  loungeAccessTokens: z.number(),
  journeyLoungeAccessEnabled: z.boolean(),
  journeyLoungeAccessTokens: z.number(),
  journeyLoungeAccessUnlimited: z.boolean(),
  journeyLoungeAccessCommunicatedDepartureThresholdHours: z.number(),
  loungeAccessCommunicatedDepartureThresholdHours: z.number(),
  departureThresholdHours: z.number(),
  freeCompensationIncluded: z.boolean()
});
const membershipSchema = z.object({
  active: z.boolean(),
  autoRenewal: z.boolean(),
  canceled: z.boolean(),
  displayName: z.string(),
  endDate: z.string(),
  fromCustomer: z.boolean(),
  onGracePeriod: z.boolean(),
  productName: z.nativeEnum(ProductName).or(z.string()),
  startDate: z.string(),
  tier: z.nativeEnum(MembershipTier).or(z.string()),
  paymentMethodAttached: z.boolean()
});
const carRentalSchema = z.object({
  code: z.string(),
  discount: z.number()
});
const fastTrackSchema = z.object({
  code: z.string(),
  discount: z.number()
});
const loungePassSchema$1 = z.object({
  discount: z.number()
});
const esimSchema = z.object({
  code: z.string(),
  discount: z.number(),
  type: z.string()
});
const perksSchema = z.object({
  esim: esimSchema.nullable(),
  carRental: carRentalSchema.nullable(),
  airportTransfer: z.object({}).nullable(),
  fastTrack: fastTrackSchema.nullable(),
  loungePass: loungePassSchema$1.nullable()
});
const coverageSchema = z.object({
  active: z.boolean(),
  covered: z.boolean(),
  expired: z.boolean()
});
const tripSchema = z.object({
  active: z.boolean(),
  partnerSlug: z.string().nullable(),
  tier: z.nativeEnum(TripTier).nullable()
});
const userPolicySchema = z.object({
  benefits: benefitsSchema.nullable(),
  coverage: coverageSchema,
  dashboard: dashboardSchema,
  membership: membershipSchema.nullable(),
  trip: tripSchema.nullable(),
  perks: perksSchema.nullable()
});
const consentsSchema = z.object({
  marketingConsent: z.boolean(),
  whatsappConsent: z.boolean(),
  flightNotificationsConsent: z.boolean().optional().nullable()
});
const baseConsentsSchema = z.object({
  consents: consentsSchema
});
const fetchConsentsResponseSchema = baseConsentsSchema;
const updateConsentsRequestSchema = consentsSchema;
const updateConsentsResponseSchema = baseConsentsSchema;
const userProfileSchema = z.object({
  consents: consentsSchema,
  email: z.string().email(),
  firstName: z.string().nullable(),
  id: z.number(),
  lastName: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  policy: userPolicySchema,
  userProfileUuid: z.string()
});
const fetchUserProfileResponseSchema = z.object({
  userProfile: userProfileSchema
});
const loungePassSchema = z.object({
  imageUrl: z.string(),
  passCode: z.string(),
  passengerName: z.string(),
  validUntil: z.string(),
  appleWalletUrl: z.string(),
  googleWalletUrl: z.string()
});
const fetchLoungePassesResponseSchema = z.object({
  loungePasses: z.array(loungePassSchema)
});
const fetchFeatureFlagsResponseSchema = z.object({
  features: z.array(featureFlagSchema)
}).transform((data) => {
  const transformedResponse = {};
  data.features.forEach((feature) => {
    transformedResponse[feature.key] = feature.state === FeatureFlagState.ON;
  });
  return transformedResponse;
});
function fetchFeatureFlags({ client }) {
  return client.get(`/api/v2/feature_flags`).valid(fetchFeatureFlagsResponseSchema);
}
const fetchLoungesRequestSchema = z.object({
  airportCode: z.string()
});
const fetchLoungesResponseSchema = z.array(loungeSchema);
function fetchLounges({
  client,
  params
}) {
  const { airportCode } = fetchLoungesRequestSchema.parse(params);
  return client.get(`/api/v2/lounges/${airportCode}`).valid(fetchLoungesResponseSchema);
}
const fetchAirPayoutJourneysRequestSchema = z.object({
  journeyId: z.number()
});
const fetchAirPayoutJourneysResponseSchema = z.array(
  airPayoutJourneysSchema
);
function fetchAirPayoutJourneys({
  client,
  params
}) {
  const { journeyId } = fetchAirPayoutJourneysRequestSchema.parse(params);
  return client.get(`/api/v2/air_bundle/journeys/${journeyId}/air_payout_journeys`).valid(fetchAirPayoutJourneysResponseSchema);
}
const fetchAirLuggageJourneysRequestSchema = z.object({
  journeyId: z.number()
});
const fetchAirLuggageJourneysResponseSchema = z.array(
  airLuggageJourneysSchema
);
function fetchAirLuggageJourneys({
  client,
  params
}) {
  const { journeyId } = fetchAirLuggageJourneysRequestSchema.parse(params);
  return client.get(`/api/v2/air_bundle/journeys/${journeyId}/air_luggage_journeys`).valid(fetchAirLuggageJourneysResponseSchema);
}
function triggerAirPayoutJourneys({
  client,
  params
}) {
  const { journeyId, token, countryId, bankFields, airPayoutJourneys } = airPayoutsTriggerRequestSchema.parse(params);
  const payload = { bankFields, countryId, airPayoutJourneys };
  return client.url(`/api/v2/air_bundle/journeys/${journeyId}/air_payout_journeys/payout`).query({ token }).snakeCaseBody(payload).post().valid(journeyWithMetaSchema);
}
function triggerAirLuggageJourneys({
  client,
  params
}) {
  const { journeyId, token, countryId, bankFields, airLuggageJourneys } = airLuggagesTriggerRequestSchema.parse(params);
  const payload = { bankFields, countryId, airLuggageJourneys };
  return client.url(`/api/v2/air_bundle/journeys/${journeyId}/air_luggage_journeys/payout`).query({ token }).snakeCaseBody(payload).post().valid(journeyWithMetaSchema);
}
function updateAirPayoutJourneys({
  client,
  params
}) {
  const { journeyId, token, countryId, bankFields } = airPayoutsUpdateRequestSchema.parse(params);
  const payload = { bankFields, countryId };
  return client.url(
    `/api/v2/air_bundle/journeys/${journeyId}/air_payout_journeys/payout_update`
  ).snakeCaseBody(payload).query({ token }).patch().valid(journeySchema);
}
function updateAirLuggageJourneys({
  client,
  params
}) {
  const { journeyId, token, transactionReference, countryId, bankFields } = airLuggagesUpdateRequestSchema.parse(params);
  const payload = { bankFields, countryId, transactionReference };
  return client.url(
    `/api/v2/air_bundle/journeys/${journeyId}/air_luggage_journeys/payout_update`
  ).query({ token }).snakeCaseBody(payload).patch().valid(journeySchema);
}
function getPresignedUrl({
  client,
  params
}) {
  return client.url(`/api/v2/s3/presigned_url`).snakeCaseBody(airBundlePresignedUrlSchema.parse(params)).post().valid(airBundlePresignedUrlResponseSchema);
}
async function uploadAirBundleDocument({
  client,
  params
}) {
  const { file, insuranceType, documentType, resourceId, insuranceId } = airBundleDocumentUploadSchema.parse(params);
  const apiParams = {
    fileType: file.type,
    insuranceType,
    documentType,
    resourceId,
    insuranceId
  };
  return await getPresignedUrl({ client, params: apiParams }).then(
    async (apiResponse) => {
      const { presignedUrl, fileName } = airBundlePresignedUrlResponseSchema.parse(apiResponse);
      return await createHTTPClient().url(presignedUrl).body(file).put().res((s3response) => {
        if (s3response.status === 200) {
          return fileName;
        }
        throw new Error("Failed to upload document");
      });
    }
  );
}
function createVerificationSession({
  client,
  params
}) {
  return client.url(`/api/v2/air_bundle/identity_verifications`).snakeCaseBody(verificationSchema.parse(params)).post().valid(verificationResponseSchema);
}
const postPromoCodeRequestSchema = z.object({
  productName: z.string(),
  promoCode: z.string(),
  paymentIntentId: z.string(),
  currency: z.string()
});
const postPromoCodeResponseSchema = z.object({
  currency: z.string(),
  discountValue: z.string(),
  discountedPrice: z.string(),
  productPrice: z.string()
});
function postPromoCode({
  client,
  params
}) {
  return client.url(`/api/v2/promo_campaigns/apply_code`).snakeCaseBody(postPromoCodeRequestSchema.parse(params)).post().valid(postPromoCodeResponseSchema);
}
const fetchJourneyRequestSchema = z.object({
  id: z.number()
});
function fetchJourney({
  client,
  params
}) {
  const { id } = fetchJourneyRequestSchema.parse(params);
  return client.get(`/api/v2/journeys/${id}`).valid(journeySchema);
}
const fetchJourneysResponseSchema = z.array(journeySchema);
function fetchJourneys({ client }) {
  return client.get(`/api/v2/journeys`).valid(fetchJourneysResponseSchema);
}
const fetchCheckDeletableJourneyRequestSchema = z.object({
  id: z.number()
});
function fetchCheckDeletableJourney({
  client,
  params
}) {
  const { id } = fetchCheckDeletableJourneyRequestSchema.parse(params);
  return client.get(`/api/v2/journeys/${id}/check_deletable`).valid(journeyCheckDeletableSchema);
}
const deleteJourneyRequestSchema = z.object({
  id: z.number()
});
function deleteJourney({
  client,
  params
}) {
  const { id } = deleteJourneyRequestSchema.parse(params);
  return client.url(`/api/v2/journeys/${id}`).delete().text();
}
const updateJourneySchema = z.object({
  name: z.string()
});
const updateJourneyRequestSchema = z.object({
  id: z.number(),
  payload: updateJourneySchema
});
function updateJourney({
  client,
  params
}) {
  const { id, payload } = updateJourneyRequestSchema.parse(params);
  return client.url(`/api/v2/journeys/${id}`).patch(payload).valid(journeySchema);
}
const minDateSchema = z.union([z.date(), z.string(), z.null()]);
const maxDateSchema = z.union([z.date(), z.string(), z.null()]);
const editableFlightSchema = z.object({
  id: z.number(),
  departureAirport: airportSchema.nullable(),
  departureAirportCode: z.string().nullable(),
  arrivalAirport: airportSchema.nullable(),
  arrivalAirportCode: z.string().nullable(),
  airline: airlineSchema.nullable(),
  airlineIdentifier: z.string().nullable(),
  flightNumber: z.string().nullable(),
  localDepartureDate: z.coerce.date().nullable(),
  metadata: z.object({
    minDate: minDateSchema,
    maxDate: maxDateSchema
  })
});
const editableFellowPassengerSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string()
});
const editableItinerarySchema = z.object({
  id: z.number(),
  fellowPassengers: z.array(editableFellowPassengerSchema),
  flights: z.array(editableFlightSchema)
});
const editableJourneySchema = z.object({
  name: z.string().nullable().optional(),
  itineraries: z.array(editableItinerarySchema),
  applyAirLuggage: z.boolean()
});
const editableJourneyRequestSchema = z.object({
  token: z.string(),
  payload: editableJourneySchema
});
const addJourneyResponseSchema = journeySchema;
function addJourney({
  client,
  params
}) {
  const { token, payload } = editableJourneyRequestSchema.parse(params);
  return client.url(`/api/v2/journeys`).query({ token }).snakeCaseBody(payload).post().valid(addJourneyResponseSchema);
}
const postDashboardVisitedResponseSchema = z.object({});
function postDashboardVisited({ client }) {
  return client.url("/api/v2/user_profiles/dashboard_visited").patch({}).valid(postDashboardVisitedResponseSchema);
}
const postPersonalDetailsProvidedResponseSchema = z.object({});
function postPersonalDetailsProvided({ client }) {
  return client.url("/api/v2/user_profiles/personal_details_provided").patch({}).valid(postPersonalDetailsProvidedResponseSchema);
}
function fetchConsents({ client }) {
  return client.get("/api/v2/user_profiles/consents").valid(fetchConsentsResponseSchema);
}
function updateConsents({
  client,
  params
}) {
  const payload = consentsSchema.parse(params);
  return client.url("/api/v2/user_profiles/consents").snakeCaseBody(payload).patch().valid(updateConsentsResponseSchema);
}
function fetchUserProfile({ client }) {
  return client.get("/api/v2/user_profiles/me").valid(fetchUserProfileResponseSchema);
}
const fetchAirBundleSubscriptionResponseSchema = z.object({
  airPayoutCreditsLeft: z.number(),
  airLuggageCreditsLeft: z.number(),
  airPayoutUnlimitedAccess: z.boolean(),
  airLuggageUnlimitedAccess: z.boolean(),
  airPayoutIpidVersion: z.string(),
  airLuggageIpidVersion: z.string(),
  validUntil: z.string()
});
function fetchAirBundleSubscription({ client }) {
  return client.get("/api/v2/user_policy/air_bundle_subscription").valid(fetchAirBundleSubscriptionResponseSchema);
}
const cancelMembershipRequestSchema = z.object({});
const cancelMembershipResponseSchema = z.object({});
function cancelMembership({
  client,
  params
}) {
  return client.url(`/api/v2/membership/cancel`).patch(cancelMembershipRequestSchema.parse(params)).valid(cancelMembershipResponseSchema);
}
function fetchBillingManagement({ client }) {
  return client.get("/api/v2/billing_management").valid(fetchBillingManagementSchema);
}
const updateBillingAddressResponseSchema = z.object({});
const updateBillingAddressSchema = z.object({
  name: z.string(),
  address: z.object({
    line1: z.string(),
    line2: z.string().nullable(),
    city: z.string(),
    country: z.string(),
    postal_code: z.string().nullable(),
    state: z.string().nullable()
  })
});
const updateBillingAddressRequestSchema = z.object({
  payload: updateBillingAddressSchema
});
function updateBillingAddress({
  client,
  params
}) {
  const { payload } = updateBillingAddressRequestSchema.parse(params);
  return client.url("/api/v2/billing_management/update_address").patch(payload).valid(updateBillingAddressResponseSchema);
}
const updateCreditCardResponseSchema = z.object({
  clientSecret: z.string(),
  setupIntentId: z.string()
});
const updateCreditCardRequestSchema = z.object({});
function updateCreditCard({
  client,
  params
}) {
  return client.url("/api/v2/billing_management").post(updateCreditCardRequestSchema.parse(params)).valid(updateCreditCardResponseSchema);
}
const fetchLoungePassesRequestSchema = z.object({
  token: z.string()
});
function fetchLoungePasses({
  client,
  params
}) {
  const { token } = fetchLoungePassesRequestSchema.parse(params);
  return client.url(`/api/v2/user_profiles/lounge_passes`).query({ token }).get().valid(fetchLoungePassesResponseSchema);
}
const upgradeOffersItemSchema = z.object({
  code: z.string(),
  discountedPrice: z.string().nullable(),
  productPrice: z.string(),
  productName: z.string(),
  discountValue: z.string().nullable()
});
const fetchUpgradeOffersResponseSchema = z.object({
  upgradeOffer: upgradeOffersItemSchema,
  renewOffer: upgradeOffersItemSchema
});
const fetchUpgradeOffersRequestSchema = z.object({
  currency: z.string()
});
function fetchUpgradeOffers({
  client,
  params
}) {
  const { currency } = fetchUpgradeOffersRequestSchema.parse(params);
  return client.url("/api/v2/offers/upgrade").query({ currency }).get().valid(fetchUpgradeOffersResponseSchema);
}
const recordAhpInteractionRequestSchema = z.object({
  actionType: z.string(),
  resourceType: z.string(),
  resourceId: z.number()
});
const recordAhpInteractionResponseSchema = z.object({});
function recordAhpInteraction({
  client,
  params
}) {
  return client.url(`/api/v2/ahp_interactions`).snakeCaseBody(recordAhpInteractionRequestSchema.parse(params)).post().valid(recordAhpInteractionResponseSchema);
}
const createPaymentIntentResponseSchema = z.object({
  clientSecret: z.string()
});
const createPaymentIntentSchema = z.object({
  email: z.string(),
  productName: z.nativeEnum(ProductName).or(z.string()),
  currency: z.string(),
  recurring: z.boolean(),
  channel: z.string(),
  locale: z.string(),
  tripUuid: z.string().optional(),
  upgrade: z.boolean().optional().default(false)
});
const orderSchema = z.object({
  currency: z.string(),
  discountAmount: z.any().pipe(z.coerce.number()).nullable().optional(),
  identifier: z.string(),
  paidAmount: z.any().pipe(z.coerce.number()).nullable().optional(),
  productName: z.nativeEnum(ProductName).or(z.string()),
  totalAmount: z.any().pipe(z.coerce.number()).nullable().optional()
});
const paymentConfirmationResponseSchema = z.object({
  order: orderSchema
});
const getPaymentConfirmationSchema = z.object({
  id: z.string()
});
const paymentStatusResponseSchema = z.object({
  paymentIntentId: z.string(),
  status: z.nativeEnum(StripePaymentStatues),
  errorCode: z.string().nullable().optional()
});
const getPaymentStatusSchema = z.object({
  id: z.string()
});
const updatePaymentIntentResponseSchema = z.object({
  currency: z.string(),
  discountValue: z.any().pipe(z.coerce.number()).nullable().optional(),
  discountedPrice: z.any().pipe(z.coerce.number()).nullable().optional(),
  productPrice: z.any().pipe(z.coerce.number()).nullable().optional()
});
const updatePaymentIntentSchema = z.object({
  currency: z.string(),
  upgrade: z.boolean().optional().default(false),
  id: z.string()
});
function createStripeCheckout({
  client,
  params
}) {
  const payload = createPaymentIntentSchema.parse(params);
  return client.url(`/api/v2/stripe_checkouts`).snakeCaseBody(payload).post().valid(createPaymentIntentResponseSchema);
}
function getPaymentConfirmation({
  client,
  params
}) {
  const { id } = getPaymentConfirmationSchema.parse(params);
  return client.get(`/api/v2/payments_confirmations/${id}`).valid(paymentConfirmationResponseSchema);
}
function getPaymentStatus({
  client,
  params
}) {
  const { id } = getPaymentStatusSchema.parse(params);
  return client.get(`/api/v2/stripe_checkouts/${id}`).valid(paymentStatusResponseSchema);
}
function updateStripeCheckout({
  client,
  params
}) {
  const { id, ...payload } = updatePaymentIntentSchema.parse(params);
  return client.url(`/api/v2/stripe_checkouts/${id}`).snakeCaseBody(payload).patch().valid(updatePaymentIntentResponseSchema);
}
function createBaseAhPlusHTTPClient({
  caller: caller$1,
  baseURL,
  options
}) {
  return createHTTPClient({
    baseURL,
    options: {
      credentials: "include",
      ...options
    }
  }).addon(caller).addon(valid()).addon(queryStringAddon).addon(snakeCaseBody()).caller(caller$1);
}
export {
  l as ActionTypeAhpInteraction,
  ActivationStatus,
  k as AirBundleDocumentFile,
  AirBundleDocumentParam,
  AirBundleRejectionReason,
  AirLuggageStatus,
  AirPayoutCoverage,
  AirPayoutDisruption,
  AirPayoutEligibleReason,
  AirPayoutStatus,
  B as BANK_INPUTS,
  CheckDeletableReason,
  FeatureFlagState,
  InsuranceType,
  MembershipTier,
  h as PaymentFrequency,
  j as ProductDisplayName,
  ProductName,
  i as ProductPerk,
  R as ResourceTypeAhpInteraction,
  StripePaymentStatues,
  TripTier,
  activateTrip,
  activateTripRequestSchema,
  addJourney,
  addJourneyResponseSchema,
  airBundleDocumentUploadSchema,
  airBundlePresignedUrlResponseSchema,
  airBundlePresignedUrlSchema,
  airLuggageJourneysSchema,
  airLuggagesTriggerRequestSchema,
  airLuggagesUpdateRequestSchema,
  airPayoutJourneysSchema,
  airPayoutRequestSchema,
  airPayoutsTriggerRequestSchema,
  airPayoutsUpdateRequestSchema,
  airRejectionsSchema,
  airlineSchema,
  airportSchema,
  bankFieldsSchema,
  billingAddress,
  cancelMembership,
  cancelMembershipRequestSchema,
  cancelMembershipResponseSchema,
  consentsSchema,
  createBaseAhPlusHTTPClient,
  createStripeCheckout,
  createVerificationSession,
  deleteJourney,
  deleteJourneyRequestSchema,
  editableFellowPassengerSchema,
  editableFlightSchema,
  editableItinerarySchema,
  editableJourneyRequestSchema,
  editableJourneySchema,
  featureFlagSchema,
  fellowPassengerSchema,
  fetchAirBundleSubscription,
  fetchAirBundleSubscriptionResponseSchema,
  fetchAirLuggageJourneys,
  fetchAirLuggageJourneysRequestSchema,
  fetchAirLuggageJourneysResponseSchema,
  fetchAirPayoutJourneys,
  fetchAirPayoutJourneysRequestSchema,
  fetchAirPayoutJourneysResponseSchema,
  fetchBillingManagement,
  fetchBillingManagementSchema,
  fetchCheckDeletableJourney,
  fetchCheckDeletableJourneyRequestSchema,
  fetchConsents,
  fetchConsentsResponseSchema,
  fetchFeatureFlags,
  fetchFeatureFlagsResponseSchema,
  fetchJourney,
  fetchJourneyRequestSchema,
  fetchJourneys,
  fetchJourneysResponseSchema,
  fetchLoungePasses,
  fetchLoungePassesRequestSchema,
  fetchLoungePassesResponseSchema,
  fetchLounges,
  fetchLoungesRequestSchema,
  fetchLoungesResponseSchema,
  b as fetchOffers,
  a as fetchOffersRequestSchema,
  f as fetchOffersResponseSchema,
  fetchTrip,
  fetchTripRequestSchema,
  fetchUpgradeOffers,
  fetchUpgradeOffersRequestSchema,
  fetchUpgradeOffersResponseSchema,
  fetchUserProfile,
  fetchUserProfileResponseSchema,
  flightSchema,
  flightStatusSchema,
  getPaymentConfirmation,
  getPaymentStatus,
  itinerarySchema,
  journeyCheckDeletableSchema,
  journeySchema,
  journeyWithMetaSchema,
  loungeSchema,
  m as makeFetchOffersRoute,
  o as offerSchema,
  payment,
  postDashboardVisited,
  postDashboardVisitedResponseSchema,
  postPersonalDetailsProvided,
  postPersonalDetailsProvidedResponseSchema,
  postPromoCode,
  postPromoCodeRequestSchema,
  postPromoCodeResponseSchema,
  recordAhpInteraction,
  recordAhpInteractionRequestSchema,
  recordAhpInteractionResponseSchema,
  subscription,
  triggerAirLuggageJourneys,
  triggerAirPayoutJourneys,
  tripResponseSchema,
  tripSchema$1 as tripSchema,
  updateAirLuggageJourneys,
  updateAirPayoutJourneys,
  updateBillingAddress,
  updateBillingAddressRequestSchema,
  updateBillingAddressResponseSchema,
  updateBillingAddressSchema,
  updateConsents,
  updateConsentsRequestSchema,
  updateConsentsResponseSchema,
  updateCreditCard,
  updateCreditCardRequestSchema,
  updateCreditCardResponseSchema,
  updateJourney,
  updateJourneyRequestSchema,
  updateJourneySchema,
  updateStripeCheckout,
  upgradeOffersItemSchema,
  uploadAirBundleDocument,
  userPolicySchema,
  userProfileSchema,
  validateActivationToken,
  validateActivationTokenRequestSchema,
  validateActivationTokenResponseSchema,
  verificationResponseSchema,
  verificationSchema
};
//# sourceMappingURL=index.js.map
